import React, { useState, useEffect, useCallback } from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  Button, 
  Box,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  Alert,
  LinearProgress,
  Fade,
  Grow
} from '@mui/material';
import { 
  CheckCircleOutline, 
  Cancel,
  Timer,
  EmojiEvents
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getWords, updateUserScore } from '../../services/gameService';
import { motion, AnimatePresence } from 'framer-motion';

const MotionCard = motion(Card);
const MotionPaper = motion(Paper);

function SingleMode() {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [words, setWords] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const [timeLeft, setTimeLeft] = useState(15);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [currentWord, setCurrentWord] = useState(null);

  const handleGameOver = useCallback(async () => {
    try {
      await updateUserScore(currentUser.uid, currentUser.score + score);
      setGameOver(true);
    } catch (error) {
      console.error('Error updating score:', error);
    }
  }, [currentUser.uid, currentUser.score, score]);

  const handleAnswer = useCallback(() => {
    if (!selectedAnswer) return;
    
    const word = words[currentWordIndex];
    setCurrentWord(word);
    const isAnswerCorrect = selectedAnswer === 'correct';
    setIsCorrect(isAnswerCorrect);
    setShowResult(true);

    if (isAnswerCorrect) {
      setScore(prev => prev + 10);
    }

    setTimeout(() => {
      setShowResult(false);
      setSelectedAnswer('');
      setTimeLeft(15);

      if (currentWordIndex < words.length - 1) {
        setCurrentWordIndex(prev => prev + 1);
        setCurrentWord(words[currentWordIndex + 1]);
      } else {
        handleGameOver();
      }
    }, 1500);
  }, [currentWordIndex, selectedAnswer, words, handleGameOver]);

  useEffect(() => {
    const loadWords = async () => {
      try {
        const wordList = await getWords(currentUser.level);
        setWords(wordList);
        setCurrentWord(wordList[0]);
        setLoading(false);
      } catch (error) {
        console.error('Error loading words:', error);
      }
    };

    loadWords();
  }, [currentUser.level]);

  useEffect(() => {
    if (!gameOver && !loading && !showResult) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            handleAnswer();
            return 15;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [gameOver, loading, showResult, handleAnswer]);

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography>Loading your game...</Typography>
          <LinearProgress sx={{ mt: 2 }} />
        </Box>
      </Container>
    );
  }

  if (gameOver) {
    return (
      <Fade in timeout={800}>
        <Container maxWidth="sm">
          <MotionPaper
            elevation={3}
            sx={{ p: 4, mt: 4, textAlign: 'center' }}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <EmojiEvents sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              Oyun Bitti!
            </Typography>
            <Typography variant="h5" gutterBottom color="primary">
              Toplam Puan: {score}
            </Typography>
            <Box sx={{ mt: 4 }}>
              <Button
                variant="contained"
                onClick={() => navigate('/dashboard')}
                sx={{ 
                  mr: 2,
                  background: 'linear-gradient(45deg, #00bf0d 30%, #4cf13d 90%)',
                  boxShadow: '0 3px 5px 2px rgba(0, 191, 13, .3)',
                }}
              >
                Ana Sayfaya Dön
              </Button>
              <Button
                variant="outlined"
                onClick={() => window.location.reload()}
                sx={{ 
                  borderColor: '#00bf0d',
                  color: '#00bf0d',
                  '&:hover': {
                    borderColor: '#00bf0d',
                    backgroundColor: 'rgba(0, 191, 13, 0.1)',
                  }
                }}
              >
                Tekrar Oyna
              </Button>
            </Box>
          </MotionPaper>
        </Container>
      </Fade>
    );
  }

  return (
    <Container maxWidth="sm">
      <MotionPaper 
        elevation={3} 
        sx={{ p: 4, mt: 4 }}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Seviye {currentUser.level} - Kelime {currentWordIndex + 1}/{words.length}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Timer sx={{ color: 'primary.main', mr: 1 }} />
            <Typography color="primary" sx={{ fontWeight: 600 }}>
              {timeLeft}s
            </Typography>
          </Box>
        </Box>

        <LinearProgress 
          variant="determinate" 
          value={(timeLeft / 15) * 100}
          sx={{ 
            mb: 3, 
            height: 6, 
            borderRadius: 3,
            bgcolor: 'rgba(0, 191, 13, 0.1)',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(45deg, #00bf0d 30%, #4cf13d 90%)',
              borderRadius: 3
            }
          }} 
        />
        
        <AnimatePresence mode="wait">
          {currentWord && (
            <MotionCard
              key={currentWordIndex}
              initial={{ x: 300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -300, opacity: 0 }}
              transition={{ duration: 0.5 }}
              sx={{ mb: 3 }}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography 
                  variant="h3" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 700,
                    background: 'linear-gradient(45deg, #00bf0d, #4cf13d)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  {currentWord.english}
                </Typography>
                <Typography 
                  variant="h4" 
                  color="text.secondary"
                  sx={{ fontWeight: 500 }}
                >
                  {currentWord.turkish}
                </Typography>
              </CardContent>
            </MotionCard>
          )}
        </AnimatePresence>

        {showResult && (
          <Grow in>
            <Alert 
              icon={isCorrect ? <CheckCircleOutline /> : <Cancel />}
              severity={isCorrect ? "success" : "error"}
              sx={{ mb: 3 }}
            >
              {isCorrect ? "Doğru! +10 puan" : "Yanlış cevap!"}
            </Alert>
          </Grow>
        )}

        <RadioGroup
          value={selectedAnswer}
          onChange={(e) => setSelectedAnswer(e.target.value)}
          sx={{ mb: 3 }}
        >
          <FormControlLabel 
            value="correct" 
            control={
              <Radio 
                sx={{
                  '&.Mui-checked': {
                    color: 'primary.main',
                  },
                }}
              />
            } 
            label="Doğru Çeviri" 
          />
          <FormControlLabel 
            value="incorrect" 
            control={
              <Radio 
                sx={{
                  '&.Mui-checked': {
                    color: 'primary.main',
                  },
                }}
              />
            } 
            label="Yanlış Çeviri" 
          />
        </RadioGroup>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Score: {score}
          </Typography>
          <Button
            variant="contained"
            disabled={!selectedAnswer || showResult}
            onClick={handleAnswer}
            sx={{
              background: 'linear-gradient(45deg, #00bf0d 30%, #4cf13d 90%)',
              boxShadow: '0 3px 5px 2px rgba(0, 191, 13, .3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #00bf0d 60%, #4cf13d 90%)',
                boxShadow: '0 4px 10px 2px rgba(0, 191, 13, .4)',
              }
            }}
          >
            Sonraki Kelime
          </Button>
        </Box>
      </MotionPaper>
    </Container>
  );
}

export default SingleMode;
